import { useState } from 'react'
import type { Lead } from '../../types/crm'
import {
  editableSourceFields,
  LEAD_SOURCE_LABELS,
  LeadSourceError,
  saveLeadSource,
  sourceValuesOf,
  type LeadSourceField,
  type LeadSourceValues,
} from './leadSourceClient'

const INPUT_TYPES: Partial<Record<LeadSourceField, string>> = { email: 'email', phone: 'tel', website: 'url' }
const MAX_LENGTHS: Record<LeadSourceField, number> = {
  name: 200, contact: 200, website: 500, email: 254, phone: 40, city: 120, state: 120, facebook: 500, instagram: 500, linkedin: 500,
}

/**
 * The Organization section in edit state. Only fields with a real column in
 * the lead's source sheet are shown; the draft survives a failed save.
 */
export function LeadOrganizationEdit({
  lead,
  onCancel,
  onSaved,
}: {
  lead: Lead
  onCancel: () => void
  onSaved: (values: LeadSourceValues) => void
}) {
  const fields = editableSourceFields(lead)
  const [initial] = useState(() => sourceValuesOf(lead))
  const [draft, setDraft] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ message: string; field: LeadSourceField | null } | null>(null)

  const changes = fields.reduce<LeadSourceValues>(
    (changed, field) => (draft[field].trim() !== initial[field].trim() ? { ...changed, [field]: draft[field].trim() } : changed),
    {},
  )
  const dirty = Object.keys(changes).length > 0

  async function submit() {
    if (!dirty || saving) return
    setError(null)
    setSaving(true)
    try {
      onSaved(await saveLeadSource(lead, changes))
    } catch (cause) {
      setError(
        cause instanceof LeadSourceError
          ? { message: cause.message, field: cause.field }
          : { message: 'The spreadsheet could not be updated. Please try again.', field: null },
      )
      setSaving(false)
    }
  }

  return (
    <form
      aria-label="Edit organization details"
      className="lead-crm-form lead-source-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <div className="lead-source-grid">
        {fields.map((field) => {
          const id = `lead-source-${field}`
          const invalid = error?.field === field
          return (
            <div className={`lead-crm-field${field === 'city' || field === 'state' ? '' : ' lead-source-wide'}`} key={field}>
              <label htmlFor={id}>{LEAD_SOURCE_LABELS[field]}</label>
              <input
                aria-describedby={invalid ? `${id}-error` : undefined}
                aria-invalid={invalid || undefined}
                autoFocus={field === fields[0]}
                disabled={saving}
                id={id}
                maxLength={MAX_LENGTHS[field]}
                onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
                type={INPUT_TYPES[field] ?? 'text'}
                value={draft[field]}
              />
              {invalid ? <p className="lead-crm-error" id={`${id}-error`}>{error.message}</p> : null}
            </div>
          )
        })}
      </div>

      {error && !error.field ? <p className="lead-crm-error" role="alert">{error.message}</p> : null}
      {error?.field ? <p className="sr-only" role="alert">{error.message}</p> : null}

      <div className="lead-crm-form-actions">
        <button className="button button-secondary lead-crm-small" disabled={!dirty || saving} type="submit">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        <button className="lead-crm-action" disabled={saving} onClick={onCancel} type="button">
          Cancel
        </button>
      </div>
    </form>
  )
}
